import { sanitizeAssistantContent } from 'utils/maggiePresentation'

const HISTORY_VERSION = 1
const DEFAULT_HISTORY_TTL_MS = 2 * 60 * 60 * 1000
const MIN_HISTORY_TTL_MS = 5 * 60 * 1000
const MAX_HISTORY_TTL_MS = 24 * 60 * 60 * 1000
const MAX_STORED_MESSAGES = 25
const MAX_CONTEXT_SUMMARY_LENGTH = 1600

const historyTtlMs = () => {
  const configured = Number(process.env.REACT_APP_MAG_CHAT_HISTORY_TTL_MS)
  if (!Number.isFinite(configured)) return DEFAULT_HISTORY_TTL_MS
  return Math.min(Math.max(configured, MIN_HISTORY_TTL_MS), MAX_HISTORY_TTL_MS)
}

export const historyStorageKey = userId => `mag.chat.history.v${HISTORY_VERSION}:${userId}`

const sanitizeCitation = citation => ({
  id: String(citation?.id || '').slice(0, 32),
  title: String(citation?.title || '').slice(0, 255),
  url: citation?.url ? String(citation.url).slice(0, 2048) : null,
  contentType: citation?.contentType ? String(citation.contentType).slice(0, 128) : null,
  contentId: citation?.contentId || null,
  moduleId: citation?.moduleId || null,
  lessonId: citation?.lessonId || null,
  fileId: citation?.fileId || null,
  page: citation?.page || null,
  activityDate: citation?.activityDate ? String(citation.activityDate).slice(0, 40) : null,
  sourceType: citation?.sourceType ? String(citation.sourceType).slice(0, 64) : null,
})

const sanitizeMessage = message => {
  if (!['assistant', 'user'].includes(message?.role)) return null
  const content = (message.role === 'assistant' ? sanitizeAssistantContent(message.content) : String(message.content || '')).slice(0, message.role === 'assistant' ? 2000 : 500)
  const contentKey = message?.contentKey ? String(message.contentKey).slice(0, 128) : null
  if (!content && !contentKey) return null

  return {
    id: String(message.id || `${message.role}-${Date.now()}`).slice(0, 160),
    role: message.role,
    content,
    contentKey,
    createdAt: String(message.createdAt || new Date().toISOString()).slice(0, 40),
    excludeFromContext: message?.excludeFromContext === true,
    requestId: message?.requestId ? String(message.requestId).slice(0, 128) : null,
    outcome: message?.outcome ? String(message.outcome).slice(0, 32) : null,
    action: ['ANSWER', 'CLARIFY', 'NOT_FOUND', 'BLOCKED'].includes(message?.action) ? message.action : null,
    clarificationAttempt: message?.action === 'CLARIFY' && Number.isInteger(message?.clarificationAttempt)
      ? Math.min(Math.max(message.clarificationAttempt, 1), 3)
      : null,
    conversational: message?.conversational === true,
    resourceDiscovery: message?.resourceDiscovery ? {
      introduction: sanitizeAssistantContent(message.resourceDiscovery.introduction).slice(0, 500),
      followUp: sanitizeAssistantContent(message.resourceDiscovery.followUp).slice(0, 220),
    } : null,
    language: message?.language ? String(message.language).slice(0, 16) : null,
    citations: Array.isArray(message?.citations) ? message.citations.slice(0, 8).map(sanitizeCitation) : [],
    feedback: message?.feedback ? {
      rating: String(message.feedback.rating || '').slice(0, 16),
      reason: String(message.feedback.reason || '').slice(0, 32),
    } : null,
  }
}

const sanitizeContextSummary = contextSummary => {
  const content = String(contextSummary?.content || '').trim().slice(0, MAX_CONTEXT_SUMMARY_LENGTH)
  const throughMessageId = String(contextSummary?.throughMessageId || '').slice(0, 160)
  if (!content || !/^[a-zA-Z0-9_-]{1,160}$/.test(throughMessageId)) return null
  return { content, throughMessageId }
}

export const readChatHistory = (userId, now = Date.now()) => {
  if (!userId || typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(historyStorageKey(userId))
    if (!raw) return null
    const stored = JSON.parse(raw)
    if (stored?.version !== HISTORY_VERSION || Number(stored?.expiresAt) <= now) {
      window.sessionStorage.removeItem(historyStorageKey(userId))
      return null
    }
    const messages = Array.isArray(stored.messages)
      ? stored.messages.map(sanitizeMessage).filter(Boolean).slice(-MAX_STORED_MESSAGES)
      : []
    const conversationId = String(stored.conversationId || '')
    if (!messages.length || !/^[a-zA-Z0-9_-]{1,128}$/.test(conversationId)) return null
    return {
      conversationState: typeof stored.conversationState === 'string' ? stored.conversationState.slice(0, 16000) : '',
      contextSummary: sanitizeContextSummary(stored.contextSummary),
      conversationId,
      messages,
    }
  } catch (_error) {
    window.sessionStorage.removeItem(historyStorageKey(userId))
    return null
  }
}

export const saveChatHistory = ({ contextSummary, conversationId, conversationState, messages, userId }, now = Date.now()) => {
  if (!userId || typeof window === 'undefined') return false
  try {
    const safeMessages = (Array.isArray(messages) ? messages : [])
      .map(sanitizeMessage)
      .filter(Boolean)
      .slice(-MAX_STORED_MESSAGES)
    window.sessionStorage.setItem(historyStorageKey(userId), JSON.stringify({
      version: HISTORY_VERSION,
      conversationState: typeof conversationState === 'string' ? conversationState.slice(0, 16000) : '',
      contextSummary: sanitizeContextSummary(contextSummary),
      conversationId: String(conversationId || '').slice(0, 128),
      messages: safeMessages,
      expiresAt: now + historyTtlMs(),
    }))
    return true
  } catch (_error) {
    return false
  }
}

export const HISTORY_DEFAULT_TTL_MS = DEFAULT_HISTORY_TTL_MS
